import { Info as InfoIcon } from 'lucide-react'
import { useScopeStore } from '../../store'
import { getSectionById, getGroupById } from '../../schema'
import { TriStateRadio, PercentageSplitInput, KYB_ENTITY_TYPE_SPLIT_FIELDS } from './shared'

export function AdvancedKybSection() {
  const { scope, updateKybConfig, updateEntityTypeSplit } = useScopeStore()
  const { kybConfig } = scope
  const disabled = scope.finalized

  // --- KYB fields ---
  const entityTypesSection = getSectionById('kyb_entity_types')
  const entityTypesGroup = getGroupById('kyb_entity_types_group')

  return (
    <>
      {/* Entity Types — Percentage Split */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {entityTypesSection?.formTitle || entityTypesGroup?.formTitle || 'Entity Types'}
        </h4>
      </div>
      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
        <div className="mb-4">
          <p className="text-sm text-[var(--text-secondary)] mb-2">
            Do you know the approximate percentage split of business entity types?
          </p>
          <TriStateRadio
            name="entity-types-enabled"
            value={kybConfig.entityTypesEnabled}
            onChange={(v) => updateKybConfig('entityTypesEnabled', v)}
            disabled={disabled}
          />
        </div>

        {kybConfig.entityTypesEnabled === 'yes' && (
          <>
            <p className="text-xs text-[var(--text-muted)] mb-3">
              Enter the approximate percentage for each business entity type.
            </p>
            <PercentageSplitInput
              fields={KYB_ENTITY_TYPE_SPLIT_FIELDS}
              values={kybConfig.entityTypeSplit as unknown as Record<string, number>}
              onChange={(key, value) => updateEntityTypeSplit(key as keyof typeof kybConfig.entityTypeSplit, value)}
              disabled={disabled}
            />

            {(kybConfig.entityTypeSplit.otherPercent > 0) && (
              <div className="mt-4">
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  Other Entity Type Description
                </label>
                <input
                  type="text"
                  className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  placeholder="Describe other entity type"
                  value={kybConfig.otherEntityType}
                  onChange={(e) => updateKybConfig('otherEntityType', e.target.value)}
                  disabled={disabled}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* KYB Data Aging */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <div className="pt-2 mb-4">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">KYB Data Aging</h4>
        </div>

        <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <InfoIcon size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-[var(--text-primary)]">
            <p className="mb-2">
              KYB aging determines how long business verification results remain valid before requiring re-verification.
              When a check workflow starts, the system reloads past results and applies aging rules.
            </p>
            <p className="mb-2 font-medium">Default: 12 months (1 year)</p>
            <p className="text-[var(--text-muted)] text-[10px] leading-relaxed">
              Note: Results may be automatically invalidated when key business data changes (e.g., director changes, ABN status updates).
            </p>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={kybConfig.agingEnabled}
              onChange={(e) => updateKybConfig('agingEnabled', e.target.checked)}
              disabled={disabled}
              className="rounded border-[var(--border-color)] bg-[var(--bg-primary)] text-blue-500 focus:ring-blue-500"
            />
            Use Custom Aging Period
          </label>

          {kybConfig.agingEnabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Aging Period (Months)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={kybConfig.agingPeriodMonths}
                    onChange={(e) => updateKybConfig('agingPeriodMonths', parseInt(e.target.value) || 12)}
                    disabled={disabled}
                    className="w-32 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  />
                  <span className="text-sm text-[var(--text-secondary)]">months</span>
                  <span className="text-xs text-[var(--text-muted)]">
                    ({Math.round((kybConfig.agingPeriodMonths / 12) * 10) / 10}{' '}
                    {kybConfig.agingPeriodMonths === 12 ? 'year' : 'years'})
                  </span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  KYB results older than {kybConfig.agingPeriodMonths} months will require re-verification
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Additional Aging Configuration (Optional)
                </label>
                <textarea
                  value={kybConfig.agingCustomConfig}
                  onChange={(e) => updateKybConfig('agingCustomConfig', e.target.value)}
                  disabled={disabled}
                  placeholder="Enter any custom aging rules or specific requirements for different business check types"
                  rows={3}
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-sm"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Specify if you need different aging periods for specific check types or special requirements
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
